import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FirebaseService } from '@vsolv/packages/firebase/web';
import { SecurityService } from '@wsphere/staff/web';
import { FeatureFlagsService } from '@wsphere/warranties/web';
import { combineLatest, distinctUntilChanged, map, startWith, switchMap } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-web-settings-page',
  templateUrl: './settings.page.html',
  standalone: false,
})
export class SettingsPage {
  private authSvc = inject(FirebaseService);
  private securitySvc = inject(SecurityService);
  private featureFlagsSvc = inject(FeatureFlagsService);

  // Global wildcard only, matching the guard + backend (a scoped wildcard must not surface admin tabs).
  private hasGlobalAccess$ = this.authSvc.getAuthId$().pipe(
    distinctUntilChanged(),
    switchMap(() => this.securitySvc.hasAccess('*', null)),
    startWith(false)
  );

  // The Roles tab (custom-role editing) is admin-only and only relevant when the tenant has custom roles enabled.
  private customRolesEnabled$ = this.featureFlagsSvc.isEnabled('CUSTOM_ROLES').pipe(startWith(false));

  tabs$ = combineLatest([this.hasGlobalAccess$, this.customRolesEnabled$]).pipe(
    map(([hasGlobalAccess, customRolesEnabled]) => [
      { label: 'Organization', routerLink: '.' },
      { label: 'Team', routerLink: 'team' },
      ...(hasGlobalAccess && customRolesEnabled ? [{ label: 'Roles', routerLink: 'roles' }] : []),
      // Bundles only group roles, so they stay available when custom-role editing is off.
      ...(hasGlobalAccess ? [{ label: 'Role Bundles', routerLink: 'role-bundles' }] : []),
      { label: 'Apps', routerLink: 'apps' },
      { label: 'Communications', routerLink: 'communications' },
      { label: 'Developer', routerLink: 'developer' },
      { label: 'Feature Flags', routerLink: 'feature-flags' },
      ...(hasGlobalAccess
        ? [
            { label: 'Assets', routerLink: 'assets', exact: false },
            { label: 'Emails', routerLink: 'emails' },
            { label: 'EDI', routerLink: 'edi' },
          ]
        : []),
    ])
  );
}
